import { FormEvent, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Mode = 'login' | 'register';

const PASSWORD_POLICY =
  'Mot de passe : 10 caractères minimum, avec au moins une majuscule, une minuscule et un chiffre.';

function passwordPolicyError(pwd: string): string | null {
  if (pwd.length < 10) return PASSWORD_POLICY;
  if (!/[A-Z]/.test(pwd)) return PASSWORD_POLICY;
  if (!/[a-z]/.test(pwd)) return PASSWORD_POLICY;
  if (!/[0-9]/.test(pwd)) return PASSWORD_POLICY;
  return null;
}

export default function Login() {
  const { user, login, refresh } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Statut auth (register ouvert ou pas, bootstrap mode)
  const [registerOpen, setRegisterOpen] = useState<boolean>(false);
  const [bootstrap, setBootstrap] = useState<boolean>(false);

  useEffect(() => {
    api
      .get<{ register_open: boolean; bootstrap_mode: boolean }>('/auth/status')
      .then((r) => {
        setRegisterOpen(r.register_open);
        setBootstrap(r.bootstrap_mode);
        // Si register fermé, force le mode login
        if (!r.register_open) setMode('login');
      })
      .catch(() => {
        setRegisterOpen(false);
      });
  }, []);

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (mode === 'register') {
      const policyErr = passwordPolicyError(password);
      if (policyErr) {
        setError(policyErr);
        return;
      }
    }
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await api.post('/auth/register', { name, email, password });
        await refresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action impossible');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={onSubmit}>
        <h1>Portail BIM</h1>
        <p className="sub">
          {mode === 'login'
            ? 'Connecte-toi pour retrouver tes projets.'
            : bootstrap
              ? 'Aucun compte n\'existe encore. Crée le compte administrateur initial.'
              : 'Crée ton compte pour démarrer.'}
        </p>

        {error && <div className="error-msg">{error}</div>}

        {mode === 'register' && (
          <div className="field">
            <label htmlFor="name">Nom</label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        )}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="password">Mot de passe</label>
          <input
            id="password"
            type="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            required
            minLength={mode === 'register' ? 10 : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {mode === 'register' && (
            <div style={{ fontSize: 11, color: 'var(--c-muted)', marginTop: 4 }}>
              {PASSWORD_POLICY}
            </div>
          )}
        </div>

        <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={busy}>
          {busy
            ? mode === 'login'
              ? 'Connexion…'
              : 'Création…'
            : mode === 'login'
              ? 'Se connecter'
              : 'Créer mon compte'}
        </button>

        {registerOpen && (
          <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13 }}>
            {mode === 'login' ? (
              <>
                {bootstrap ? 'Aucun compte ?' : 'Pas encore de compte ?'}{' '}
                <button
                  type="button"
                  onClick={() => {
                    setMode('register');
                    setError(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--c-sidebar)',
                    fontWeight: 600,
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  En créer un
                </button>
              </>
            ) : (
              <>
                Déjà inscrit ?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setMode('login');
                    setError(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--c-sidebar)',
                    fontWeight: 600,
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  Se connecter
                </button>
              </>
            )}
          </div>
        )}

        {!registerOpen && mode === 'login' && (
          <div style={{ textAlign: 'center', marginTop: 14, fontSize: 12, color: 'var(--c-muted)' }}>
            L'inscription publique est désactivée. Demande à un administrateur de créer ton compte.
          </div>
        )}
      </form>
    </div>
  );
}
